/**
 * Validates ComponentInput payloads against the server's own JSON Schema (GET {ws}/schemas, AllowAnonymous,
 * RestApi.cs:268; JSON Schema 2020-12 with $defs hexColor/rangeSet/colorRangeSet/residue/size/visualization/
 * coloring/filter/component, each of filter/coloring/visualization a oneOf keyed by `kind`).
 * Snapshot: docs/look-spec/workspace-api-schemas-2026-10-02.json (copied to test-fixtures/).
 *
 * The plan (docs/PLAN.md "Open in Nanome") validates every payload before sending and degrades to add-default-only
 * when a custom component fails.
 */
import Ajv2020, { type ErrorObject, type ValidateFunction } from 'ajv/dist/2020.js';
import type { ComponentInput } from '@nanome-lite/state';
import type { ComponentSchemas } from './workspace-api';

export interface ValidationResult { ok: boolean; errors: string[] }

export type ComponentValidator = (input: ComponentInput) => ValidationResult;

const SCHEMA_ID = 'https://workspaces.nanome.ai/schemas';

/** Compile the component validator from a schemas document. Pure; cache the result (see createSchemaValidator). */
export function compileComponentValidator(schemas: ComponentSchemas): ComponentValidator {
  if (!schemas || typeof schemas !== 'object' || !schemas.$defs || !('component' in schemas.$defs)) {
    throw new Error('schemas document has no $defs.component');
  }
  const ajv = new Ajv2020({ strict: false, allErrors: true, allowUnionTypes: true });
  const root = { ...schemas, $id: SCHEMA_ID };
  delete (root as { presets?: unknown }).presets; // data, not schema
  ajv.addSchema(root, SCHEMA_ID);
  const validate: ValidateFunction = ajv.compile({ $ref: `${SCHEMA_ID}#/$defs/component` });
  return (input: ComponentInput) => {
    const ok = validate(input) as boolean;
    return { ok, errors: ok ? [] : formatErrors(validate.errors ?? []) };
  };
}

/** Human-readable, de-duplicated error lines. oneOf branches produce many lines; the top-level ones are kept. */
export function formatErrors(errors: ErrorObject[]): string[] {
  const out = new Set<string>();
  for (const e of errors) {
    const where = e.instancePath || '/';
    const extra = e.keyword === 'additionalProperties' ? ` (${String((e.params as { additionalProperty?: string }).additionalProperty)})`
      : e.keyword === 'enum' ? ` (${JSON.stringify((e.params as { allowedValues?: unknown[] }).allowedValues)})`
      : e.keyword === 'const' ? ` (${JSON.stringify((e.params as { allowedValue?: unknown }).allowedValue)})`
      : '';
    out.add(`${where} ${e.message ?? e.keyword}${extra}`);
  }
  return [...out];
}

export interface SchemaValidatorOptions {
  /** Fetches the schemas document (normally api.getSchemas). */
  fetchSchemas: () => Promise<ComponentSchemas>;
}

export interface SchemaValidator {
  /** Resolves the cached compiled validator, fetching and compiling once. A failed fetch is not cached. */
  ready(): Promise<ComponentValidator>;
  validateComponent(input: ComponentInput): Promise<ValidationResult>;
  /** Drop the cache (e.g. after the server reports an unknown field). */
  reset(): void;
}

/** A cached, lazily compiled validator bound to a schemas source. */
export function createSchemaValidator(options: SchemaValidatorOptions): SchemaValidator {
  let pending: Promise<ComponentValidator> | null = null;
  const ready = () => {
    if (!pending) {
      pending = options.fetchSchemas().then(compileComponentValidator).catch(e => { pending = null; throw e; });
    }
    return pending;
  };
  return {
    ready,
    async validateComponent(input) { return (await ready())(input); },
    reset() { pending = null; },
  };
}

/** Session-wide cache keyed by Workspace API base URL. */
const byBaseUrl = new Map<string, SchemaValidator>();

export function getSchemaValidator(baseUrl: string, fetchSchemas: () => Promise<ComponentSchemas>): SchemaValidator {
  let v = byBaseUrl.get(baseUrl);
  if (!v) { v = createSchemaValidator({ fetchSchemas }); byBaseUrl.set(baseUrl, v); }
  return v;
}

export function clearSchemaValidatorCache(): void { byBaseUrl.clear(); }

/** One-shot: validate against a schemas document already in hand (compiles each call; prefer createSchemaValidator). */
export function validateComponent(input: ComponentInput, schemas: ComponentSchemas): ValidationResult {
  return compileComponentValidator(schemas)(input);
}
